import { Fragment, useRef, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CSVLink } from "react-csv";
import { FiUpload } from "react-icons/fi";
import AuthContext from "../../../../auth/AuthContext";
import AlertService from "../../../../services/AlertService";
import AssessmentService from "../../../../services/AssessmentService";

const UploadAssessmentQuestion = () => {
  const navigate = useNavigate();
  const { id } = useParams(); 
  const authContext = useContext(AuthContext);

  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [loading, setLoading] = useState(false);
  const [buttonText, setButtonText] = useState("Upload");
  const [results, setResults] = useState([]);

  const fileInputRef = useRef(null);

  const headers = [
    { label: "Question", key: "question" },
    { label: "Option1", key: "option1" },
    { label: "Option1Answer", key: "option1Answer" },
    { label: "Option2", key: "option2" },
    { label: "Option2Answer", key: "option2Answer" },
    { label: "Option3", key: "option3" },
    { label: "Option3Answer", key: "option3Answer" },
    { label: "Option4", key: "option4" },
    { label: "Option4Answer", key: "option4Answer" },
    { label: "Option5", key: "option5" },
    { label: "Option5Answer", key: "option5Answer" },
    { label: "Difficulty", key: "difficulty" },
    { label: "HasGraceMark", key: "hasGraceMark" },
    { label: "Status", key: "status" },
    { label: "Reason", key: "reason" },
  ];

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setFileError("");
      setResults([]); 
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setFileError("Please select a file before uploading.");
      return;
    }

    setFileError("");
    setLoading(true);
    setButtonText("Processing...");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await AssessmentService.ImportQuestions(
        id,
        formData,
        authContext
      );

      const response =
        res?.data?.isSuccess !== undefined ? res.data : res;

      if (response.isSuccess) {
        AlertService.success("Questions imported successfully");
        setResults(response.data || []); 
      } else {
        AlertService.error(response.errorMessage || "Import failed");
      }
    } catch (err) {
      console.error("Import failed:", err);
      AlertService.error("Failed to import questions");
    } finally {
      setLoading(false);
      setButtonText("Upload");
    }
  };

  return (
    <Fragment>
      <div className="mt-3">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload */}
          <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-4">
            <label className="md:col-span-2 text-base font-medium">
              Select Excel File
            </label>

            <div className="md:col-span-10">
              <div className="flex">
                <input
                  type="text"
                  readOnly
                  value={file ? file.name : ""}
                  placeholder="Upload file"
                  className={`
                    flex-1 px-4 py-3 border rounded-l-md
                    bg-white dark:bg-slate-900
                    dark:border-slate-700
                    ${fileError ? "border-red-500" : "border-gray-300"}
                  `}
                />

                <button
                  type="button"
                  onClick={() => fileInputRef.current.click()}
                  disabled={loading}
                  className="
                    px-4 flex items-center justify-center
                    bg-[#0e3a46] text-white text-base hover:opacity-90
                    rounded-r-md
                    disabled:opacity-60
                  "
                >
                  <FiUpload className="text-lg" />
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xls,.xlsx,.csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>

              {fileError && (
                <p className="text-red-500 text-base mt-1">
                  {fileError}
                </p>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={loading}
              className="
                px-6 py-2 rounded-md border
                border-gray-400
                hover:bg-gray-200
                dark:border-slate-600
                dark:hover:bg-slate-800
              "
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="
                px-6 py-2 rounded-md
                bg-[#0e3a46] text-white text-base hover:opacity-90
                disabled:opacity-60
              "
            >
              {buttonText}
            </button>
            {!loading && results.length > 0 && (
              <CSVLink
                data={results}
                headers={headers}
                filename="Assessment-MCQ-Upload-Results.csv"
                className="
                  px-6 py-2 rounded-md
                  bg-green-600 text-white
                  hover:bg-green-700
                "
              >
                Download Results
              </CSVLink>
            )}
          </div>
        </form>
      </div>
    </Fragment>
  );
};

export default UploadAssessmentQuestion;
